import React, { useState, useEffect } from 'react';
import EditableSection from './EditableSection';
import RichTextEditor from './RichTextEditor';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const ProjectsEditor = ({ data, onSave, onRemove }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(data || []);
  const [expandedIndex, setExpandedIndex] = useState(0);

  useEffect(() => {
    if (data) setEditData(data);
  }, [data]);

  const handleSave = () => {
    onSave(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(data || []);
    setIsEditing(false);
  };

  const addItem = () => {
    setEditData([...editData, { title: '', tech_stack: '', description: '', links: [] }]);
    setExpandedIndex(editData.length);
  };

  const updateItem = (index, field, value) => {
    const newData = [...editData];
    newData[index] = { ...newData[index], [field]: value };
    setEditData(newData);
  };

  const removeItem = (index) => {
    const newData = editData.filter((_, i) => i !== index);
    setEditData(newData);
    if (expandedIndex === index) {
      setExpandedIndex(Math.max(0, index - 1));
    } else if (expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const toggleExpand = (index) => {
    if (expandedIndex === index) setExpandedIndex(-1);
    else setExpandedIndex(index);
  };

  const renderDescription = (desc) => {
    if (Array.isArray(desc)) {
      if (desc.length === 0) return '';
      return '<ul><li>' + desc.join('</li><li>') + '</li></ul>';
    }
    return desc || '';
  };

  const updateLink = (projIndex, linkIndex, field, value) => {
    const newData = [...editData];
    const newLinks = [...(newData[projIndex].links || [])];
    newLinks[linkIndex] = { ...newLinks[linkIndex], [field]: value };
    newData[projIndex] = { ...newData[projIndex], links: newLinks };
    setEditData(newData);
  };
  
  const addLink = (projIndex) => {
    const newData = [...editData];
    const newLinks = [...(newData[projIndex].links || []), { label: '', url: '' }];
    newData[projIndex] = { ...newData[projIndex], links: newLinks };
    setEditData(newData);
  };

  const removeLink = (projIndex, linkIndex) => {
    const newData = [...editData];
    const newLinks = (newData[projIndex].links || []).filter((_, i) => i !== linkIndex);
    newData[projIndex] = { ...newData[projIndex], links: newLinks };
    setEditData(newData);
  };

  return (
    <EditableSection 
      title="Projects"
      isEditing={isEditing} 
      onEdit={() => setIsEditing(true)} 
      onCancel={handleCancel} 
      onSave={handleSave}
      onRemove={onRemove}
    >
      {isEditing ? (
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200 font-sans">
          <div className="space-y-4 mb-4">
            {editData.map((proj, idx) => (
              <div key={idx} className="border border-gray-300 rounded bg-white overflow-hidden shadow-sm">
                <div 
                  className="bg-gray-100 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => toggleExpand(idx)}
                >
                  <div className="font-medium text-gray-800 flex-1 truncate">
                    {proj.title || 'New Project'}
                  </div>
                  <div className="flex gap-2 items-center">
                    <button 
                      onClick={(e) => { e.stopPropagation(); removeItem(idx); }} 
                      className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                      title="Remove Project"
                    >
                      <Trash2 size={16} />
                    </button>
                    {expandedIndex === idx ? <ChevronUp size={20} className="text-gray-500" /> : <ChevronDown size={20} className="text-gray-500" />}
                  </div>
                </div>
                
                {expandedIndex === idx && (
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Project Title</label>
                      <input 
                        type="text" 
                        value={proj.title || ''} 
                        onChange={(e) => updateItem(idx, 'title', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        placeholder="e.g. Personal Portfolio"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tech Stack</label>
                      <input
                        type="text"
                        value={proj.tech_stack || ''}
                        onChange={(e) => updateItem(idx, 'tech_stack', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        placeholder="e.g. React, Firebase, Tailwind CSS"
                      />
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">Project Links</label>
                        <button onClick={() => addLink(idx)} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs font-medium">
                          <Plus size={14} /> Add Link
                        </button>
                      </div>
                      <div className="space-y-2">
                        {(proj.links || []).map((link, lIdx) => (
                          <div key={lIdx} className="flex gap-2 items-center">
                            <input 
                              type="text" 
                              value={link.label} 
                              onChange={(e) => updateLink(idx, lIdx, 'label', e.target.value)}
                              className="w-1/3 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                              placeholder="Label (e.g. GitHub)"
                            />
                            <input 
                              type="text" 
                              value={link.url} 
                              onChange={(e) => updateLink(idx, lIdx, 'url', e.target.value)}
                              className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                              placeholder="URL (e.g. https://github.com/...)"
                            />
                            <button onClick={() => removeLink(idx, lIdx)} className="text-red-500 hover:text-red-700 p-2 transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description (Bullets)</label>
                      <RichTextEditor 
                        value={renderDescription(proj.description)} 
                        onChange={(val) => updateItem(idx, 'description', val)} 
                        placeholder="Describe your project, technologies used, format as a bulleted list..."
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            {editData.length === 0 && <p className="text-sm text-gray-500 italic py-2">No projects added yet.</p>}
          </div>
          <button onClick={addItem} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium">
            <Plus size={16} /> Add Project
          </button>
        </div>
      ) : (
        <div className="resume-content text-[15px] space-y-4 text-gray-900">
          {(!data || data.length === 0) ? (
            <p className="text-gray-400 italic">No projects provided.</p>
          ) : (
            data.map((proj, idx) => (
              <div key={idx} className="mb-3">
                <div className="flex justify-between items-baseline mb-1">
                  <div className="font-bold">
                    {proj.title} 
                    {proj.links && proj.links.length > 0 && (
                      <span className="font-normal text-[14.5px] ml-2">
                        {proj.links.map((link, lIdx) => (
                          <React.Fragment key={lIdx}>
                            {lIdx > 0 && <span className="mx-1 text-gray-400">|</span>}
                            <a href={link.url} target="_blank" rel="noreferrer" className="text-gray-700 hover:underline">
                              {link.label}
                            </a>
                          </React.Fragment>
                        ))}
                      </span>
                    )}
                  </div>
                </div>
                {proj.tech_stack && proj.tech_stack.trim() !== '' && (
                  <div className="text-[13px] text-gray-900 mb-[2px]">
                    <span className="font-bold">Tech Stack: </span>
                    <span>{proj.tech_stack}</span>
                  </div>
                )}
                <div 
                  className="pl-4 prose-sm max-w-none leading-snug style-quill-lists"
                  dangerouslySetInnerHTML={{ __html: renderDescription(proj.description) }}
                />
              </div>
            ))
          )}
        </div>
      )}
    </EditableSection>
  );
};

export default ProjectsEditor;
